import { Button, ICON_COLOR, IconButton } from '@zep-us/zus';
import { BookmarkIcon, PlayIcon, SquareStackIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { COLLECTION_COPY } from '@/shared/features/quiz-collection';

/**
 * MobileViewerCTAButtons Props
 */
export interface MobileViewerCTAButtonsProps {
  /** 복사 버튼 클릭 핸들러 */
  onDuplicate: () => void;
  /** 입장 버튼 클릭 핸들러 */
  onEnter: () => void;
  /** 컬렉션에 저장 핸들러 */
  onSaveToCollection?: () => void;
}

/**
 * MobileViewerCTAButtons - 모바일용 뷰어 CTA 버튼
 *
 * @description
 * 모바일 하단 고정 영역에 최적화된 뷰어용 CTA 입니다 (Figma 34388:41137).
 * 폭을 나눠 갖는 텍스트 버튼 둘(복사·입장) 오른쪽에, 폭이 고정된 컬렉션 저장
 * 아이콘 버튼이 붙는다.
 *
 * @example
 * ```tsx
 * <MobileViewerCTAButtons
 *   onDuplicate={handleDuplicate}
 *   onEnter={handleEnter}
 * />
 * ```
 */
export const MobileViewerCTAButtons = ({
  onDuplicate,
  onEnter,
  onSaveToCollection
}: MobileViewerCTAButtonsProps) => {
  const {
    t
  } = useTranslation(['common']);
  return <div className="flex w-full gap-sm" data-sentry-component="MobileViewerCTAButtons" data-sentry-source-file="MobileViewerCTAButtons.tsx">
      <Button size="md" intent="gradient" variant="contained" leftSlot={<SquareStackIcon size="sm" fill={ICON_COLOR.inverse} />} onClick={onDuplicate} className="flex-1" data-sentry-element="Button" data-sentry-source-file="MobileViewerCTAButtons.tsx">
        {t('detail.copyToMyQuiz')}
      </Button>
      <Button size="md" variant="contained" intent="alternative" onClick={onEnter} leftSlot={<PlayIcon size="sm" fill={ICON_COLOR.strong} />} className="flex-1" data-sentry-element="Button" data-sentry-source-file="MobileViewerCTAButtons.tsx">
        {t('detail.cta.tutorial')}
      </Button>
      {/*
        시안(34388:41137)상 컬렉션 진입은 두 텍스트 버튼 **오른쪽 끝**의 정사각 아이콘
        버튼이다. 텍스트 버튼을 하나 더 넣으면 좁은 화면에서 글자가 잘리므로 아이콘으로
        두고 폭을 고정하며, 의미는 aria-label 로 남긴다.
       */}
      {onSaveToCollection && <IconButton size="md" variant="outlined" intent="neutral" className="shrink-0" aria-label={COLLECTION_COPY.saveToCollection} onClick={onSaveToCollection}>
          <BookmarkIcon size="sm" fill={ICON_COLOR.neutral} />
        </IconButton>}
    </div>;
};